import {
  AsendiaServices,
  AustraliaPostServices,
  FirstMileServices,
  GlsServices,
  LandmarkGlobalServices,
  OnTracServices,
  PassportGlobalServices,
  RoyalMailV3Services,
  Service,
  VehoServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  SpeeDeeServices,
  supportedEPostGlobalServices,
  UpsServices,
  UspsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { isEnumValue } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";
import {
  OutboundRateTable,
  RateTableType,
  RateTableValueMode,
} from "./rate-table";

export function rateTableCarrierLabel(carrier: RateTableCarrier): string {
  switch (carrier) {
    case RateTableCarrier.USPS:
      return "USPS";
    case RateTableCarrier.USPS_RETURNS:
      return "USPS Returns";
    case RateTableCarrier.FEDEX:
      return "FedEx";
    case RateTableCarrier.UPS:
      return "UPS";
    case RateTableCarrier.UPS_SUREPOST:
      return "UPS SurePost";
    case RateTableCarrier.Amazon:
      return "Amazon";
    case RateTableCarrier.DHL_EXPRESS:
      return "DHL Express";
    case RateTableCarrier.VEHO:
      return "Veho";
    case RateTableCarrier.FIRST_MILE:
      return "First Mile";
    case RateTableCarrier.DHL_ECS:
      return "DHL eCommerce";
    case RateTableCarrier.AUSTRALIA_POST:
      return "Australia Post";
    case RateTableCarrier.CANADA_POST:
      return "Canada Post";
    case RateTableCarrier.APC:
      return "APC";
    case RateTableCarrier.GLS:
      return "GLS";
    case RateTableCarrier.ASENDIA:
      return "Asendia";
    case RateTableCarrier.ROYAL_MAIL_V3:
      return "Royal Mail V3";
    case RateTableCarrier.SPEE_DEE:
      return "SpeeDee";
    case RateTableCarrier.PASSPORT_GLOBAL:
      return "Passport Global";
    case RateTableCarrier.LANDMARK_GLOBAL:
      return "Landmark Global";
    case RateTableCarrier.ONTRAC:
      return "OnTrac";
    case RateTableCarrier.UNIUNI:
      return "UniUni";
    case RateTableCarrier.EPOST_GLOBAL:
      return "ePost Global";
    case RateTableCarrier.GOFO:
      return "GOFO";
    default:
      assertNever(carrier);
  }
}

export enum USPSRateTableServiceLevel {
  GroundAdvantage = "Ground Advantage",
  Priority = "Priority",
  Express = "Express",
  FirstClass = "First Class",
  FirstClassMailInternational = "First Class Mail International",
}

export enum USPSReturnsRateTableServiceLevel {
  GroundAdvantageReturn = "GroundAdvantageReturn",
  GroundReturn = "GroundReturn",
  PriorityMailReturn = "PriorityMailReturn",
  FirstClassPkgReturn = "FirstClassPkgReturn",
}

export enum FedexRateTableServiceLevel {
  FedexGround = "Ground",
  SmartPost = "Smart Post",
  OneRateExpressSaver = "One Rate Express Saver",
  OneRateTwoDay = "One Rate Two Day",
  OneRateTwoDayAM = "One Rate Two Day AM",
  OneRateStandard = "One Rate Standard Overnight",
  OneRatePriorityOvernight = "One Rate Priority Overnight",
  OneRateFirstOvernight = "One Rate First Overnight",
  InternationalPriority = "International Priority",
  InternationalPriorityExpress = "International Priority Express",
  InternationalConnectPlus = "International Connect Plus",
  InternationalFirst = "International First",
  InternationalEconomy = "International Economy",
  EuropeFirstInternationalPriority = "Europe First International Priority",
  InternationalEconomyFreight = "International Economy Freight",
  InternationalPriorityFreight = "International Priority Freight",
}

const nonSurePostUpsServices = Object.values(UpsServices).filter(
  (service) =>
    service !== UpsServices.SurePostUnder1Lb &&
    service !== UpsServices.SurePostOver1Lb,
);

export type UpsRateTableServiceLevel =
  | (typeof nonSurePostUpsServices)[number]
  | "Default";

export type UpsSurePostRateTableServiceLevel =
  | UpsServices.SurePostUnder1Lb
  | UpsServices.SurePostOver1Lb
  | "Default";

export type AmazonRateTableServiceLevel = AmazonServices | "Default";

export type DhlExpressRateTableServiceLevel = DhlExpressServices | "Default";

export type VehoRateTableServiceLevel = VehoServices | "Default";

export type FirstMileRateTableServiceLevel = FirstMileServices | "Default";

export type DhlEcsRateTableServiceLevel = DhlEcsServices | "Default";

export type ApcRateTableServiceLevel = ApcServices | "Default";

export type AustraliaPostRateTableServiceLevel =
  | AustraliaPostServices
  | "Default";

export type CanadaPostRateTableServiceLevel = CanadaPostServices | "Default";

export type GlsRateTableServiceLevel = GlsServices | "Default";

export type AsendiaRateTableServiceLevel = AsendiaServices | "Default";
export type RoyalMailV3RateTableServiceLevel = RoyalMailV3Services | "Default";

export type SpeeDeeRateTableServiceLevel = SpeeDeeServices | "Default";

export type PassportGlobalRateTableServiceLevel =
  | PassportGlobalServices
  | "Default";

export type LandmarkGlobalRateTableServiceLevel =
  | LandmarkGlobalServices
  | "Default";

export type OnTracRateTableServiceLevel = OnTracServices | "Default";

export type UniUniRateTableServiceLevel = "Default";

export type EPostGlobalRateTableServiceLevel = EPostGlobalServices | "Default";

export type GofoRateTableServiceLevel = "Default";

export type RateTableServiceLevel =
  | USPSRateTableServiceLevel
  | USPSReturnsRateTableServiceLevel
  | FedexRateTableServiceLevel
  | UpsRateTableServiceLevel
  | AmazonRateTableServiceLevel
  | UpsSurePostRateTableServiceLevel
  | DhlExpressRateTableServiceLevel
  | DhlEcsRateTableServiceLevel
  | VehoRateTableServiceLevel
  | FirstMileRateTableServiceLevel
  | AustraliaPostRateTableServiceLevel
  | CanadaPostRateTableServiceLevel
  | ApcRateTableServiceLevel
  | GlsRateTableServiceLevel
  | AsendiaRateTableServiceLevel
  | RoyalMailV3RateTableServiceLevel
  | SpeeDeeRateTableServiceLevel
  | PassportGlobalRateTableServiceLevel
  | LandmarkGlobalRateTableServiceLevel
  | OnTracRateTableServiceLevel
  | EPostGlobalRateTableServiceLevel
  | UniUniRateTableServiceLevel
  | GofoRateTableServiceLevel;

interface RateTableServiceLevels {
  [RateTableCarrier.USPS]: USPSRateTableServiceLevel;
  [RateTableCarrier.USPS_RETURNS]: USPSReturnsRateTableServiceLevel;
  [RateTableCarrier.FEDEX]: FedexRateTableServiceLevel;
  [RateTableCarrier.UPS]: UpsRateTableServiceLevel;
  [RateTableCarrier.Amazon]: AmazonRateTableServiceLevel;
  [RateTableCarrier.UPS_SUREPOST]: UpsSurePostRateTableServiceLevel;
  [RateTableCarrier.DHL_EXPRESS]: DhlExpressRateTableServiceLevel;
  [RateTableCarrier.VEHO]: VehoRateTableServiceLevel;
  [RateTableCarrier.FIRST_MILE]: FirstMileRateTableServiceLevel;
  [RateTableCarrier.AUSTRALIA_POST]: AustraliaPostRateTableServiceLevel;
  [RateTableCarrier.CANADA_POST]: CanadaPostRateTableServiceLevel;
  [RateTableCarrier.DHL_ECS]: DhlEcsRateTableServiceLevel;
  [RateTableCarrier.APC]: ApcRateTableServiceLevel;
  [RateTableCarrier.GLS]: GlsRateTableServiceLevel;
  [RateTableCarrier.ASENDIA]: AsendiaRateTableServiceLevel;
  [RateTableCarrier.ROYAL_MAIL_V3]: RoyalMailV3RateTableServiceLevel;
  [RateTableCarrier.SPEE_DEE]: SpeeDeeRateTableServiceLevel;
  [RateTableCarrier.PASSPORT_GLOBAL]: PassportGlobalRateTableServiceLevel;
  [RateTableCarrier.LANDMARK_GLOBAL]: LandmarkGlobalRateTableServiceLevel;
  [RateTableCarrier.ONTRAC]: OnTracRateTableServiceLevel;
  [RateTableCarrier.UNIUNI]: UniUniRateTableServiceLevel;
  [RateTableCarrier.EPOST_GLOBAL]: EPostGlobalRateTableServiceLevel;
  [RateTableCarrier.GOFO]: GofoRateTableServiceLevel;
}

export const RateTableServiceLevelsMapping: Record<
  RateTableCarrier,
  RateTableServiceLevels[RateTableCarrier][]
> = {
  [RateTableCarrier.USPS]: [
    "Default",
    ...Object.values(USPSRateTableServiceLevel),
  ],
  [RateTableCarrier.USPS_RETURNS]: [
    "Default",
    ...Object.values(USPSReturnsRateTableServiceLevel),
  ],
  [RateTableCarrier.FEDEX]: [
    "Default",
    ...Object.values(FedexRateTableServiceLevel),
  ],
  [RateTableCarrier.UPS]: ["Default", ...Object.values(nonSurePostUpsServices)],
  [RateTableCarrier.UPS_SUREPOST]: [
    "Default",
    UpsServices.SurePostUnder1Lb,
    UpsServices.SurePostOver1Lb,
  ],
  [RateTableCarrier.Amazon]: ["Default", ...Object.values(AmazonServices)],
  [RateTableCarrier.DHL_EXPRESS]: [
    "Default",
    ...Object.values(DhlExpressServices),
  ],
  [RateTableCarrier.VEHO]: ["Default", ...Object.values(VehoServices)],
  [RateTableCarrier.FIRST_MILE]: [
    "Default",
    ...Object.values(FirstMileServices),
  ],
  [RateTableCarrier.DHL_ECS]: ["Default", ...Object.values(DhlEcsServices)],
  [RateTableCarrier.AUSTRALIA_POST]: [
    "Default",
    ...Object.values(AustraliaPostServices),
  ],
  [RateTableCarrier.CANADA_POST]: [
    "Default",
    ...Object.values(CanadaPostServices),
  ],
  [RateTableCarrier.APC]: ["Default", ...Object.values(ApcServices)],
  [RateTableCarrier.GLS]: ["Default", ...Object.values(GlsServices)],
  [RateTableCarrier.ASENDIA]: ["Default", ...Object.values(AsendiaServices)],
  [RateTableCarrier.ROYAL_MAIL_V3]: [
    "Default",
    ...Object.values(RoyalMailV3Services),
  ],
  [RateTableCarrier.SPEE_DEE]: ["Default", ...Object.values(SpeeDeeServices)],
  [RateTableCarrier.PASSPORT_GLOBAL]: [
    "Default",
    ...Object.values(PassportGlobalServices),
  ],
  [RateTableCarrier.LANDMARK_GLOBAL]: [
    "Default",
    ...Object.values(LandmarkGlobalServices),
  ],
  [RateTableCarrier.ONTRAC]: ["Default", ...Object.values(OnTracServices)],
  [RateTableCarrier.UNIUNI]: ["Default"],
  [RateTableCarrier.EPOST_GLOBAL]: ["Default", ...supportedEPostGlobalServices],
  [RateTableCarrier.GOFO]: ["Default"],
};

export interface RateTableKeys {
  carrier: RateTableCarrier;
  serviceLevel: RateTableServiceLevel;
}

// Determines what rate table keys to use given the carrier and service level from easypost
export function getRateTableKeysFromRate(
  carrier: Carriers,
  serviceLevel: Service,
): RateTableKeys {
  switch (carrier) {
    case Carriers.FEDEX_SMART_POST:
    case Carriers.FEDEX:
      switch (serviceLevel) {
        case FedexServices.FEDEX_GROUND:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.FedexGround,
          };
        case FedexServices.SMART_POST:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.SmartPost,
          };
        case FedexServices.FEDEX_EXPRESS_SAVER:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRateExpressSaver,
          };
        case FedexServices.FEDEX_2_DAY:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRateTwoDay,
          };
        case FedexServices.FEDEX_2_DAY_AM:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRateTwoDayAM,
          };
        case FedexServices.PRIORITY_OVERNIGHT:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRatePriorityOvernight,
          };
        case FedexServices.STANDARD_OVERNIGHT:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRateStandard,
          };
        case FedexServices.FIRST_OVERNIGHT:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.OneRateFirstOvernight,
          };
        case FedexServices.INTERNATIONAL_PRIORITY:
        case FedexServices.FEDEX_INTERNATIONAL_PRIORITY:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.InternationalPriority,
          };
        case FedexServices.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel:
              FedexRateTableServiceLevel.InternationalPriorityExpress,
          };
        case FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.InternationalConnectPlus,
          };
        case FedexServices.INTERNATIONAL_FIRST:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.InternationalFirst,
          };
        case FedexServices.INTERNATIONAL_ECONOMY:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel: FedexRateTableServiceLevel.InternationalEconomy,
          };
        case FedexServices.EUROPE_FIRST_INTERNATIONAL_PRIORITY:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel:
              FedexRateTableServiceLevel.EuropeFirstInternationalPriority,
          };
        case FedexServices.INTERNATIONAL_ECONOMY_FREIGHT:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel:
              FedexRateTableServiceLevel.InternationalEconomyFreight,
          };
        case FedexServices.INTERNATIONAL_PRIORITY_FREIGHT:
          return {
            carrier: RateTableCarrier.FEDEX,
            serviceLevel:
              FedexRateTableServiceLevel.InternationalPriorityFreight,
          };
        default:
          return { carrier: RateTableCarrier.FEDEX, serviceLevel: "Default" };
      }
    case Carriers.USPS_RETURNS:
      switch (serviceLevel) {
        case UspsServices.GroundReturn:
          return {
            carrier: RateTableCarrier.USPS_RETURNS,
            serviceLevel: USPSReturnsRateTableServiceLevel.GroundReturn,
          };
        case UspsServices.GroundAdvantageReturn:
          return {
            carrier: RateTableCarrier.USPS_RETURNS,
            serviceLevel:
              USPSReturnsRateTableServiceLevel.GroundAdvantageReturn,
          };
        case UspsServices.PriorityMailReturn:
          return {
            carrier: RateTableCarrier.USPS_RETURNS,
            serviceLevel: USPSReturnsRateTableServiceLevel.PriorityMailReturn,
          };
        case UspsServices.FirstClassPkgReturn:
          return {
            carrier: RateTableCarrier.USPS_RETURNS,
            serviceLevel: USPSReturnsRateTableServiceLevel.FirstClassPkgReturn,
          };
        default:
          return {
            carrier: RateTableCarrier.USPS_RETURNS,
            serviceLevel: "Default",
          };
      }
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
      switch (serviceLevel) {
        case UspsServices.GroundAdvantage:
          return {
            carrier: RateTableCarrier.USPS,
            serviceLevel: USPSRateTableServiceLevel.GroundAdvantage,
          };
        case UspsServices.Priority:
          return {
            carrier: RateTableCarrier.USPS,
            serviceLevel: USPSRateTableServiceLevel.Priority,
          };
        case UspsServices.Express:
          return {
            carrier: RateTableCarrier.USPS,
            serviceLevel: USPSRateTableServiceLevel.Express,
          };
        case UspsServices.FirstClassMailInternational:
          return {
            carrier: RateTableCarrier.USPS,
            serviceLevel: USPSRateTableServiceLevel.FirstClassMailInternational,
          };
        case UspsServices.FirstClass:
          return {
            carrier: RateTableCarrier.USPS,
            serviceLevel: USPSRateTableServiceLevel.FirstClass,
          };
        default:
          return { carrier: RateTableCarrier.USPS, serviceLevel: "Default" };
      }
    case Carriers.UPS:
      if (isEnumValue(serviceLevel, UpsServices)) {
        return { carrier: RateTableCarrier.UPS, serviceLevel };
      }
      return { carrier: RateTableCarrier.UPS, serviceLevel: "Default" };
    case Carriers.UPS_SUREPOST:
      switch (serviceLevel) {
        case UpsServices.SurePostUnder1Lb:
          return {
            carrier: RateTableCarrier.UPS_SUREPOST,
            serviceLevel: UpsServices.SurePostUnder1Lb,
          };
        case UpsServices.SurePostOver1Lb:
          return {
            carrier: RateTableCarrier.UPS_SUREPOST,
            serviceLevel: UpsServices.SurePostOver1Lb,
          };
        default:
          return {
            carrier: RateTableCarrier.UPS_SUREPOST,
            serviceLevel: "Default",
          };
      }
    case Carriers.AMAZON:
      if (isEnumValue(serviceLevel, AmazonServices)) {
        return { carrier: RateTableCarrier.Amazon, serviceLevel };
      }
      return { carrier: RateTableCarrier.Amazon, serviceLevel: "Default" };
    case Carriers.DHL_EXPRESS:
      return { carrier: RateTableCarrier.DHL_EXPRESS, serviceLevel: "Default" };
    case Carriers.VEHO:
      if (isEnumValue(serviceLevel, VehoServices)) {
        return { carrier: RateTableCarrier.VEHO, serviceLevel };
      }
      return { carrier: RateTableCarrier.VEHO, serviceLevel: "Default" };
    case Carriers.FIRST_MILE:
      if (isEnumValue(serviceLevel, FirstMileServices)) {
        return { carrier: RateTableCarrier.FIRST_MILE, serviceLevel };
      }
      return { carrier: RateTableCarrier.FIRST_MILE, serviceLevel: "Default" };
    case Carriers.DHL_ECS:
      if (isEnumValue(serviceLevel, DhlEcsServices)) {
        return { carrier: RateTableCarrier.DHL_ECS, serviceLevel };
      }
      return { carrier: RateTableCarrier.DHL_ECS, serviceLevel: "Default" };
    case Carriers.AUSTRALIA_POST:
      if (isEnumValue(serviceLevel, AustraliaPostServices)) {
        return { carrier: RateTableCarrier.AUSTRALIA_POST, serviceLevel };
      }
      return {
        carrier: RateTableCarrier.AUSTRALIA_POST,
        serviceLevel: "Default",
      };
    case Carriers.CANADA_POST:
      if (isEnumValue(serviceLevel, CanadaPostServices)) {
        return { carrier: RateTableCarrier.CANADA_POST, serviceLevel };
      }
      return { carrier: RateTableCarrier.CANADA_POST, serviceLevel: "Default" };
    case Carriers.APC:
      return { carrier: RateTableCarrier.APC, serviceLevel: "Default" };
    case Carriers.GLS:
      return { carrier: RateTableCarrier.GLS, serviceLevel: "Default" };
    case Carriers.ASENDIA:
      return { carrier: RateTableCarrier.ASENDIA, serviceLevel: "Default" };
    case Carriers.ROYAL_MAIL_V3:
      return {
        carrier: RateTableCarrier.ROYAL_MAIL_V3,
        serviceLevel: "Default",
      };
    case Carriers.SPEE_DEE:
      return { carrier: RateTableCarrier.SPEE_DEE, serviceLevel: "Default" };
    case Carriers.PASSPORT_GLOBAL:
      return {
        carrier: RateTableCarrier.PASSPORT_GLOBAL,
        serviceLevel: "Default",
      };
    case Carriers.LANDMARK_GLOBAL:
      return {
        carrier: RateTableCarrier.LANDMARK_GLOBAL,
        serviceLevel: "Default",
      };
    case Carriers.ETA_EXPRESS:
      throw new Error("ETA Express is not supported for Redo rate tables");
    case Carriers.EPOST_GLOBAL:
      return {
        carrier: RateTableCarrier.EPOST_GLOBAL,
        serviceLevel: "Default",
      };
    case Carriers.ONTRAC:
      return { carrier: RateTableCarrier.ONTRAC, serviceLevel: "Default" };
    case Carriers.GLOBALE:
      throw new Error("Global-E is not supported for Redo rate tables");
    case Carriers.UPS_MAIL_INNOVATIONS:
      throw new Error(
        "UPS Mail Innovations is not supported for Redo rate tables",
      );
    case Carriers.PUROLATOR:
      throw new Error("Purolator is not supported for Redo rate tables");
    case Carriers.UNIUNI:
      return { carrier: RateTableCarrier.UNIUNI, serviceLevel: "Default" };
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      throw new Error(`${carrier} is not supported for Redo rate tables`);
    case Carriers.CANPAR_EXPRESS:
      throw new Error("Canpar Express is not supported for Redo rate tables");
    case Carriers.GOFO:
      return { carrier: RateTableCarrier.GOFO, serviceLevel: "Default" };
    default:
      assertNever(carrier);
  }
}

export function findMatchingOutboundRateTable(
  rateTableKeys: RateTableKeys,
  rateTables: OutboundRateTable[],
  valueMode: RateTableValueMode,
  rateTableType: RateTableType,
  provider: RateProvider,
): OutboundRateTable | null {
  return (
    rateTables.find(
      (table: OutboundRateTable) =>
        table.carrier === rateTableKeys.carrier &&
        table.serviceLevel === rateTableKeys.serviceLevel &&
        table.valueMode === valueMode &&
        table.rateTableType === rateTableType &&
        table.provider === provider,
    ) ?? null
  );
}
